import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Chip,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  TouchApp as ManualPaymentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { PaymentHistoryItem } from "../../../redux/types/types";
import AttendeeImportantInfoButton from "../../Attendees/AttendeeImportantInfoButton";

interface PaymentHistoryListProps {
  payments: PaymentHistoryItem[];
  total: number;
  page: number;
  rowsPerPage: number;
  orderBy: "createdAt" | "amount";
  order: "asc" | "desc";
  onRequestSort: (property: "createdAt" | "amount") => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEditManualPayment?: (payment: PaymentHistoryItem) => void;
  onDeleteManualPayment?: (payment: PaymentHistoryItem) => void;
}

const PaymentHistoryList: React.FC<PaymentHistoryListProps> = ({
  payments,
  total,
  page,
  rowsPerPage,
  orderBy,
  order,
  onRequestSort,
  onChangePage,
  onChangeRowsPerPage,
  onEditManualPayment,
  onDeleteManualPayment,
}) => {
  const intl = useIntl();

  const formatAmount = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  const getStatusColor = (status: string, type: string) => {
    if (type === "manual") return "success";
    switch (status) {
      case "SUCCESS":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string, type: string) => {
    if (type === "manual") return "Completed";
    return status;
  };

  return (
    <Paper sx={{ width: "100%", mb: 2 }}>
      <TableContainer>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
          <TableHead>
            <TableRow>
              <TableCell>
                <FormattedMessage
                  id="admin.paymentHistory.type"
                  defaultMessage="Type"
                />
              </TableCell>
              <TableCell>
                <FormattedMessage
                  id="admin.paymentHistory.attendee"
                  defaultMessage="Attendee"
                />
              </TableCell>
              <TableCell sortDirection={orderBy === "amount" ? order : false}>
                <TableSortLabel
                  active={orderBy === "amount"}
                  direction={orderBy === "amount" ? order : "asc"}
                  onClick={() => onRequestSort("amount")}
                >
                  <FormattedMessage
                    id="admin.paymentHistory.amount"
                    defaultMessage="Amount"
                  />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage
                  id="admin.paymentHistory.details"
                  defaultMessage="Details"
                />
              </TableCell>
              <TableCell>
                <FormattedMessage
                  id="admin.paymentHistory.status"
                  defaultMessage="Status"
                />
              </TableCell>
              <TableCell
                sortDirection={orderBy === "createdAt" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => onRequestSort("createdAt")}
                >
                  <FormattedMessage
                    id="admin.paymentHistory.date"
                    defaultMessage="Date"
                  />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage
                  id="admin.paymentHistory.actions"
                  defaultMessage="Actions"
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments &&
              payments.map((payment) => (
                <TableRow key={`${payment.type}-${payment.id}`} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {payment.type === "manual" ? (
                        <ManualPaymentIcon color="primary" sx={{ mr: 1 }} />
                      ) : (
                        <PaymentIcon color="secondary" sx={{ mr: 1 }} />
                      )}
                      <Chip
                        label={
                          payment.type === "manual" ? (
                            <FormattedMessage
                              id="admin.paymentHistory.manual"
                              defaultMessage="Manual"
                            />
                          ) : (
                            <FormattedMessage
                              id="admin.paymentHistory.stripe"
                              defaultMessage="Stripe"
                            />
                          )
                        }
                        size="small"
                        color={
                          payment.type === "manual" ? "primary" : "secondary"
                        }
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {payment.attendee.firstName} {payment.attendee.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payment.attendee.email}
                      </Typography>
                      <AttendeeImportantInfoButton
                        attendee={payment.attendee}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatAmount(payment.amount)}{" "}
                      {payment.currency?.toUpperCase() || "CHF"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {payment.numberOfClasses && (
                      <Typography variant="body2">
                        {payment.numberOfClasses}{" "}
                        <FormattedMessage
                          id="admin.paymentHistory.classes"
                          defaultMessage="classes"
                        />
                      </Typography>
                    )}
                    {payment.specialType && (
                      <Typography variant="body2" color="text.secondary">
                        {payment.specialType}
                      </Typography>
                    )}
                    {payment.isAdminOnly && (
                      <Chip
                        label={
                          <FormattedMessage
                            id="admin.paymentHistory.adminPrice"
                            defaultMessage="Admin Price"
                          />
                        }
                        size="small"
                        color="info"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                    {payment.type === "manual" && payment.recordedEntry && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          <FormattedMessage
                            id="admin.paymentHistory.recordedBy"
                            defaultMessage="Recorded by:"
                          />
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {payment.recordedEntry.firstname}{" "}
                          {payment.recordedEntry.lastname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payment.recordedEntry.email}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(payment.status, payment.type)}
                      color={getStatusColor(payment.status, payment.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {intl.formatDate(new Date(payment.createdAt), {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {payment.type === "manual" && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip
                          title={intl.formatMessage({
                            id: "admin.paymentHistory.editPayment",
                            defaultMessage: "Edit Payment",
                          })}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onEditManualPayment?.(payment)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={intl.formatMessage({
                            id: "admin.paymentHistory.deletePayment",
                            defaultMessage: "Delete Payment",
                          })}
                        >
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeleteManualPayment?.(payment)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="admin.paymentHistory.noData"
                      defaultMessage="No payment history found"
                    />
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        labelRowsPerPage={intl.formatMessage({
          id: "admin.table.rowsPerPage",
          defaultMessage: "Rows per page:",
        })}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${intl.formatMessage({
            id: "admin.table.of",
            defaultMessage: "of",
          })} ${
            count !== -1
              ? count
              : `${intl.formatMessage({
                  id: "admin.table.moreThan",
                  defaultMessage: "more than",
                })} ${to}`
          }`
        }
      />
    </Paper>
  );
};

export default PaymentHistoryList;
